<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row dense>
      <v-col
        class="labeled-input"
        cols="12"
      >
        <span>{{ $bct(label) }}</span>
        <RequiredIndicator v-if="mandatory" />
      </v-col>

      <template v-if="description">
        <v-col
          class="fs-16 c-light-black my-1"
          cols="12"
        >
          <span>{{ $bct(description) }}</span>
        </v-col>
      </template>

      <v-col cols="12">
        <v-tooltip
          :disabled="!tooltipText"
          location="bottom"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <v-text-field
                @click="dateDialogIsOpen = !dateDialogIsOpen"
                @click:clear="handleUpdateModelValue(null)"
                @keypress="dateDialogIsOpen = !dateDialogIsOpen"
                :append-inner-icon="locked ? 'lock' : ''"
                :aria-label="$bct(ariaLabel)"
                :density="dense ? 'compact' : undefined"
                :disabled="disabled || locked"
                :model-value="localeDateString"
                :placeholder="disabled ? $bct(disabledMessage) : $bct(placeholderText)"
                data-cy="labeled-date-picker"
                data-testid="labeled-date-picker"
                prepend-inner-icon="event"
                variant="filled"
                clearable
                hide-details
                readonly
                tile
                v-bind="filteredAttrs"
              />
            </span>
          </template>
          <span v-if="tooltipText">
            {{ $bct(tooltipText) }}
          </span>
        </v-tooltip>

        <v-dialog
          v-model="dateDialogIsOpen"
          width="320px"
        >
          <DatePicker
            v-model="dateModel"
            :allowed-dates="allowedDates"
            :max="max"
            :min="min"
            scrollable
          />
        </v-dialog>
      </v-col>
    </v-row>

    <v-divider
      v-if="dividedBottom"
      class="mt-4 mb-2"
    />
  </v-col>
</template>

<script setup>
import { onUpdated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate } from 'vuetify';
import DatePicker from '@/shared/components/DatePicker.vue';
import RequiredIndicator from '@/shared/components/RequiredIndicator.vue';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';
import attrsOmitDataPrefix from '@/shared/utils/filteredAttrs';
import propsToRefs from '@/shared/utils/propsToRefs';

const attrs = useAttrs();
const $bct = inject('translator');
const { locale } = useI18n();
const vuetifyDate = useDate();

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  disabledMessage: {
    type: String,
    default: null,
  },
  disallowAfterDate: {
    type: String,
    default: null,
  },
  disallowBeforeDate: {
    type: String,
    default: null,
  },
  dividedBottom: {
    type: Boolean,
    default: false,
  },
  max: {
    default: null,
    type: String,
  },
  min: {
    default: null,
    type: String,
  },
  placeholderText: {
    type: String,
    default: null,
  },
  tooltipText: {
    type: String,
    default: null,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

defineEmits(useLabeledField.emits);

const localValue = defineModel({ type: String });

const { handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, label, ariaLabel, locked } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const filteredAttrs = ref(attrsOmitDataPrefix(attrs));
const dateDialogIsOpen = ref(false);

const localeDateString = computed(() => {
  if (!localValue.value) return null;

  return new Date(localValue.value).toLocaleDateString(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'utc',
  });
});

const dateModel = computed({
  get() {
    return localValue.value == null ? null : vuetifyDate.parseISO(localValue.value);
  },
  set(val) {
    dateDialogIsOpen.value = false;
    handleUpdateModelValue(vuetifyDate.toISO(val));
  },
});

const allowedDates = (date) => {
  let allowed = true;
  if (props.disallowBeforeDate)
    allowed = allowed && new Date(date) >= new Date(props.disallowBeforeDate);
  if (props.disallowAfterDate)
    allowed = allowed && new Date(date) <= new Date(props.disallowAfterDate);
  return allowed;
};

// attrs is not a reactive object, so we update the filteredAttrs ref on every DOM tree update
onUpdated(() => {
  filteredAttrs.value = attrsOmitDataPrefix(attrs);
});
</script>
